export type PayPayOnboardingError = "unconfirmed-email" | "not-onboarded"

export type PayPayOnboardingStatus = "success" | PayPayOnboardingError

export type PaymentStatus = "succeeded" | "pending" | "failed"

export type PayoutStatus = "paid" | "pending" | "in_transit" | "canceled" | "failed"

export const checkoutTypes = [
	"coursePurchase",
	"videoPurchase",
	"downloadablePurchase",
	"bundlePurchase",
	"sessionPurchase",
	"eventPurchase",
] as const
export type CheckoutType = (typeof checkoutTypes)[number]

export const subscriptionTypes = ["plan", "addon", "membership"] as const
export type SubscriptionType = (typeof subscriptionTypes)[number]

export interface Payment {
	id: string
	currency: string
	amount: {
		grossTotal: number
		applicationFee: number
		netTotal: number
	}
	status: PaymentStatus
	date: Date
}

export interface Payout {
	id: string
	currency: string
	amount: number
	status: PayoutStatus
	date: Date
	arrivalDate: Date
}

export interface Transaction {
	id: string
	currency: string
	amount: number
	status: "success" | "pending" | "failed"
	receipt: { status: "available"; url: string } | { status: "paypal" } | { status: "unavailable" }
	date: Date
	item: string | null
}

export interface StripePaymentInformation {
	balances: {
		currency: string
		amount: number
	}[]
	payments: Payment[]
	payouts: Payout[]
}

export interface PayPalPaymentInformation {
	balances: {
		currency: string
		amount: number
	}[]
	payments: Payment[]
}

export type PaymentInformation =
	| ({ type: "stripe" } & StripePaymentInformation)
	| ({ type: "paypal" } & PayPalPaymentInformation)

export const subscriptionPaymentIntervals = ["month", "year"] as const
export type SubscriptionPaymentInterval = (typeof subscriptionPaymentIntervals)[number]

export interface SubscriptionPlan {
	id: number
	price: { amount: number; interval: SubscriptionPaymentInterval }
}

export const cardBrands = ["amex", "diners", "discover", "jcb", "mastercard", "unionpay", "visa"] as const
export type CardBrand = (typeof cardBrands)[number]

export const subscriptionSources = ["onboarding", "email", "settings"] as const
export type SubscriptionSource = (typeof subscriptionSources)[number]

export interface PaymentMethod {
	id: string
	brand: CardBrand | null
	last4: string
	expiration: {
		month: number
		year: number
	}
	wallet:
		| "amex_express_checkout"
		| "apple_pay"
		| "google_pay"
		| "masterpass"
		| "samsung_pay"
		| "visa_checkout"
		| "link"
		| null
}

export type PlanAccess =
	| "customDomain"
	| "landingPage"
	| "videos"
	| "bundles"
	| "community"
	| "assignProducts"
	| "zapier"
	| "chapterDripping"
	| "certificates"
	| "communityChannels"
	| "noWatermark"
	| "tracking"

export interface Plan {
	id: number | null
	title: string
	description: string
	usps: string[]
	price: { month: number; year: number } | null
	commission: number
	access: { [key in PlanAccess]: boolean }
}

export interface AddonDetails {
	title: string
	description: string
	usps: string[]
	prices: { month: number; year: number }
}

export interface PayPalOrder {
	id: string
	fullPriceAmount: number
	discountAmount: number
	amount: number
	currency: string
	platformFee: number
	status: "success" | "pending"
	customerEmail: string
	date: Date
}

export type StripeDiscountCode = {
	id: string
	code: string
	name: string
	maxRedemptions: number | null
	lastRedemptionDate: Date | null
	discount: {
		type: "amount" | "percent"
		value: number
	}
	duration: { type: "repeating"; months: number } | { type: "once" | "forever" }
}

export type PaypalDiscountCode = {
	id: number
	code: string
	name: string
	maxRedemptions: number | null
	lastRedemptionDate: Date | null
	discount: { type: "amount" | "percent"; value: number }
	redemptions: number
}

export const cancelReasonIds = ["switched", "features", "ltd", "expectations", "price", "other"] as const

export type CancelReasonId = (typeof cancelReasonIds)[number]

export const cancelReasons: Record<CancelReasonId, { label: string; elaboration: string }> = {
	switched: { label: "I found another service", elaboration: "Which service are you switching to, and why?" },
	features: { label: "I don't use the features", elaboration: "Which features are you missing?" },
	ltd: { label: "I'm buying a lifetime deal", elaboration: "What made you pick the lifetime deal?" },
	expectations: {
		label: "It didn't meet my expectations",
		elaboration: "Do you miss any specific feature? Any additional feedback?",
	},
	price: { label: "The price is too high", elaboration: "What price do you think is fair?" },
	other: { label: "Other reason", elaboration: "Do you have any additional feedback?" },
}

export const addonTypes = ["noCommission", "scale"] as const
export type AddonType = (typeof addonTypes)[number]
